/**
 * Time/meta formatting helpers for the Team of Agents rows (2026-07-02
 * pane redesign). Pure functions + a light ticking hook so running rows
 * can show a live elapsed counter.
 *
 * Formatting philosophy: the meta line is scannable chrome, not prose —
 * compact unit-letters ("42s", "3m12s", "1h05m") read the same across
 * pt/en/es, so no i18n keys are needed for durations. Clock stamps use
 * the active locale via `Intl.DateTimeFormat`.
 */
import { useEffect, useState } from "react";

/** HH:MM in the user's locale for today; adds day/month when older. */
export function formatClock(ts: number, locale: string, now = Date.now()): string {
  if (!Number.isFinite(ts) || ts <= 0) return "";
  const d = new Date(ts);
  const sameDay = new Date(now).toDateString() === d.toDateString();
  if (sameDay) {
    return new Intl.DateTimeFormat(locale, {
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  }
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

/** Compact duration: "8s" · "3m12s" · "1h05m". Sub-second → "0s".
 *  Negative/NaN (clock skew, missing data) → "". */
export function formatElapsed(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m${String(s % 60).padStart(2, "0")}s`;
  const h = Math.floor(m / 60);
  return `${h}h${String(m % 60).padStart(2, "0")}m`;
}

/** "$0.031" — trims to 3 decimals, hides dust below a tenth of a cent. */
export function formatCost(usd: number | undefined): string {
  if (usd === undefined || !Number.isFinite(usd) || usd < 0.001) return "";
  return `$${usd.toFixed(3).replace(/0$/, "")}`;
}

/** Re-renders the caller every `intervalMs` while `active`; returns the
 *  current epoch. Used by running rows for the live elapsed counter —
 *  terminal rows pass `active: false` and never tick. */
export function useNow(active: boolean, intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [active, intervalMs]);
  return now;
}

/** Joins meta-line fragments with a middle dot, skipping empties. */
export function joinMeta(...parts: Array<string | undefined>): string {
  return parts.filter((p): p is string => Boolean(p && p.length)).join(" · ");
}
